import { useCallback, useEffect, useState } from 'react';
import { isNil } from 'es-toolkit';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { ClientPortalNavState } from './types';
import { GET_CLIENT_SIDEBAR_DATA } from '../../../../components/dashboard/client-sidebar-query';
import { CLIENT_PORTAL_NAV_BOOTSTRAP_TIMEOUT_MS } from './constants';

export function useClientPortalNav(): ClientPortalNavState {
  const { user } = useAuth();
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();

  const skipped = isNil(user?.id) || !canQuery;
  const { data, loading, error, refetch } = useQueryWithErrorLogging(GET_CLIENT_SIDEBAR_DATA, {
    variables: { id: user?.id, householdId: activeHouseholdId ?? undefined },
    skip: skipped,
  });

  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [waitAttempt, setWaitAttempt] = useState(0);

  useEffect(() => {
    if (!skipped) {
      setBootstrapTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      setBootstrapTimedOut(true);
    }, CLIENT_PORTAL_NAV_BOOTSTRAP_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [skipped, waitAttempt]);

  const client = data?.client;
  const household = client?.household;
  const stalledBootstrap = skipped && bootstrapTimedOut;
  const failed = !isNil(error) || (!skipped && !loading && isNil(client)) || stalledBootstrap;

  const retry = useCallback(() => {
    setBootstrapTimedOut(false);
    setWaitAttempt((attempt) => attempt + 1);
    if (!skipped) {
      void refetch();
    }
  }, [refetch, skipped]);

  const personalPortfolioId = household?.portfolios.find((portfolio) => portfolio.isPersonal)?.id;

  return {
    loading: !stalledBootstrap && (loading || skipped),
    failed,
    retry,
    personalPortfolioId: isNil(personalPortfolioId) ? null : String(personalPortfolioId),
    enableTaxTab: client?.enableTaxTab === true,
    investmentsDisabled: household?.adviceOnly === true || isNil(household?.orionHouseholdId),
  };
}
